<template>
	<div
		:class="{
			'input-wrapper': true,
			'login-wrapper': true,
			'focused': isFocused || modelValue
		}"
		@mouseenter="handleMouseEnter"
		@mouseleave="handleMouseLeave"
	>
		<textarea
			ref="textareaRef"
			:class="{
				'app-textarea': true,
				'app-textarea--error': props.error
			}"
			:style="textareaStyles"
			:id="id"
			:name="name"
			:value="modelValue"
			:placeholder="props.placeholder"
			@input="handleInput"
			@focus="onFocus"
			@blur="onBlur"
		/>
		<app-tooltip
			v-if="showClearIcon"
			class="app-textarea__clear"
			:text="clearFieldHint || $t('clear_field')"
		>
			<template #activator>
				<app-icon
					name="arrow-left"
					width="16px"
					color="#979797"
					cursor="pointer"
					@click="clearField"
					@mousedown.prevent
				/>
			</template>
		</app-tooltip>
	</div>
</template>

<script setup lang="ts">
import {
	ref,
	computed,
	nextTick
} from 'vue';
import AppIcon from '@/components/ui-kit/icon/AppIcon.vue';
import AppTooltip from '@/components/ui-kit/tooltip/AppTooltip.vue';

import '@/styles/inputs.scss';

type AppTextareaProps = {
	modelValue: string | null,
	id?: string,
	name?: string,
	height?: string | number,
	floatingLabel?: string,
	placeholder?: string,
	clearFieldHint?: string,
	error?: boolean
}

const props = withDefaults(defineProps<AppTextareaProps>(), {
	id: 'textarea-input-elm',
	name: 'textarea',
	height: '100%',
	clearFieldHint: ''
})

const emit = defineEmits([
	'update:modelValue',
	'input',
	'focus',
	'blur',
	'clear'
])

const textareaRef = ref<HTMLTextAreaElement>();
const isFocused = ref(false);
const showClearIconMouseHold = ref(false);

const textareaStyles = computed(() => ({
	height: typeof props.height === "string"
		? props.height
		: `${props.height}px`
}))

const showClearIcon = computed(() => {
	return (isFocused.value || showClearIconMouseHold.value)
		&& !!props.modelValue
		&& props.modelValue.length > 0;
})

const handleMouseEnter = () => {
	showClearIconMouseHold.value = true;
};

const handleMouseLeave = () => {
	showClearIconMouseHold.value = false;
};

const handleInput = (event: Event) => {
	const target = event.target as HTMLTextAreaElement;
	emit('update:modelValue', target.value);
	emit('input', target.value);
};

const onFocus = () => {
	isFocused.value = true;
	emit('focus');
};

const onBlur = () => {
	isFocused.value = false;
	emit('blur');
};

const clearField = () => {
	emit('update:modelValue', '');
	emit('clear');
	nextTick(() => {
		isFocused.value = true;
		textareaRef.value?.focus();
	});
};

const focus = () => {
	const el = textareaRef.value;
	if (!el) {
		return;
	}
	el.focus({ preventScroll: true });
	el.setSelectionRange(el.value.length, el.value.length);
};

defineExpose({ focus });
</script>

<style scoped lang="scss">
.app-textarea {
	width: 100%;
	padding: 8px 28px 6px 12px;
	font-size: 14px;
	color: #4D4D4D;
	min-height: 66px;
	border: 1px solid #D2D2D2;
	border-radius: 8px;

	&::placeholder {
		font-size: 15px;
		color: #979797;
	}

	&:focus {
		border: 1px solid #3F884F;
		outline: none;
	}

	&:hover {
		border: 1px solid #3F884F;
		outline: none;
	}

	&--error {
		border-color: #FF5533 !important;
	}
}

.input-wrapper {
	width: 100%;
	position: relative;
	display: inline-block;
}

.app-textarea__clear {
	position: absolute;
	top: 8px;
	right: 10px;
	z-index: 1;
}

@media (max-width: 768px) {
	.input-wrapper {
		width: 100%;
	}
}
</style>
